import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Loader2, Wallet } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;

/** Wallet balance + FA top-up / refund (same calls as the People wallet search). */
export function WalletPanel({ customerId, active = true, onChanged }) {
  const { authAxios } = useAuth();
  const [w, setW] = useState(null);
  const [mode, setMode] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [loading, setLoading] = useState(false);

  const load = () => authAxios.get(`/wallet/balance/${customerId}`).then(r => setW(r.data.wallet)).catch(() => setW(false));
  useEffect(() => { setW(null); setMode(null); load(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return toast.error("Enter a valid amount");
    if (mode === "refund" && val > w.perpetual_balance) return toast.error("Exceeds refundable balance");
    setLoading(true);
    try {
      if (mode === "topup") await authAxios.post("/wallet/topup", { customer_id: customerId, amount: val, payment_type: paymentType });
      else await authAxios.post("/wallet/refund", { customer_id: customerId, amount: val });
      toast.success(`${eur(val)} ${mode === "topup" ? "topped up" : "refunded"}!`);
      setMode(null); setAmount(""); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || (mode === "topup" ? "Top-up failed" : "Refund failed")); }
    finally { setLoading(false); }
  };

  if (w === false) return null;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm mb-4" data-testid="wallet-panel">
      <h3 className="font-bold text-sm text-[#1e3a5f] flex items-center gap-2 mb-3"><Wallet className="w-4 h-4" /> Wallet</h3>
      {!w ? <div className="py-3 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></div> : (
        <>
          <div className="grid grid-cols-3 divide-x text-center bg-slate-50 rounded-xl py-2 mb-3">
            <div><p className="text-lg font-extrabold text-[#1e3a5f]" data-testid="wallet-panel-total">{eur(w.total_balance)}</p><p className="text-slate-500 text-[10px]">Total</p></div>
            <div><p className="font-bold text-green-600" data-testid="wallet-panel-perpetual">{eur(w.perpetual_balance)}</p><p className="text-slate-500 text-[10px]">Refundable</p></div>
            <div><p className="font-bold text-amber-600">{eur(w.expiring_balance)}</p><p className="text-slate-500 text-[10px]">Expiring</p></div>
          </div>
          {!mode && active && (
            <div className="flex gap-2">
              <button onClick={() => { setMode("topup"); setAmount(""); }} className="flex-1 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-sm font-bold flex items-center justify-center gap-2" data-testid="wallet-panel-topup-btn"><Plus className="w-4 h-4" /> Top-up</button>
              {w.perpetual_balance > 0 && (
                <button onClick={() => { setMode("refund"); setAmount(w.perpetual_balance.toFixed(2)); }} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold flex items-center justify-center gap-2" data-testid="wallet-panel-refund-btn"><RefreshCw className="w-4 h-4" /> Refund</button>
              )}
            </div>
          )}
          {mode && (
            <div className={`rounded-xl p-3 ${mode === "refund" ? "bg-red-50" : "bg-slate-50"}`} data-testid={`wallet-panel-${mode}-form`}>
              {mode === "refund" && <p className="text-xs text-red-600 mb-2">Refundable: <b>{eur(w.perpetual_balance)}</b></p>}
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount" min="0" step="0.01"
                className="w-full p-2.5 rounded-xl border text-base font-bold text-center mb-2" data-testid="wallet-panel-amount" />
              {mode === "topup" && (
                <div className="flex gap-2 mb-2">
                  {["cash", "card", "bank_transfer"].map(pt => (
                    <button key={pt} onClick={() => setPaymentType(pt)} className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${paymentType === pt ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`}>{pt === "bank_transfer" ? "Transfer" : pt[0].toUpperCase() + pt.slice(1)}</button>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setMode(null)} className="flex-1 py-2 rounded-xl border bg-white text-sm font-bold text-slate-600">Cancel</button>
                <button onClick={submit} disabled={loading} className={`flex-1 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2 ${mode === "refund" ? "bg-red-500" : "bg-[#1e3a5f]"}`} data-testid="wallet-panel-confirm">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} {mode === "topup" ? "Top-up" : "Refund"} €{amount || "0"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
